import React from 'react';
import MultiSelect from '../../../tailwindComponents/ui/multi-select';
import {
    ClientDashboardData,
    AchievementFilter,
    PeriodType,
    ComparisonType
} from '../../../interfaces/dashboards/CrossClientDashboard.interface';
import CrossClientDashboardService from '../../../services/dashboards/CrossClientDashboard.service';

interface DashboardControlsProps {
    clients: ClientDashboardData[];
    selectedClientIds: number[];
    achievementFilter: AchievementFilter;
    periodType: PeriodType;
    comparisonType: ComparisonType;
    onClientSelectionChange: (clientIds: number[]) => void;
    onAchievementFilterChange: (filter: AchievementFilter) => void;
    onPeriodTypeChange: (period: PeriodType) => void;
    onComparisonTypeChange: (comparison: ComparisonType) => void;
}

const DashboardControls: React.FC<DashboardControlsProps> = ({
    clients,
    selectedClientIds,
    achievementFilter,
    periodType,
    comparisonType,
    onClientSelectionChange,
    onAchievementFilterChange,
    onPeriodTypeChange,
    onComparisonTypeChange
}) => {
    const clientOptions = clients.map(client => ({
        code: client.clientId.toString(),
        label: CrossClientDashboardService.getClientDisplayString(client)
    }));

    const selectedValues = selectedClientIds.length === 0
        ? ['ALL']
        : selectedClientIds.map(id => id.toString());

    const handleClientChange = (values: string[]) => {
        if (values.includes('ALL') || values.length === 0) {
            onClientSelectionChange([]);
        } else {
            const ids = values.map(v => parseInt(v, 10));
            onClientSelectionChange(ids);
        }
    };

    return (
        <div className="bg-white rounded border border-gray-300 px-4 py-2 mb-3 flex items-center justify-between gap-4">
            {/* Left - Client Selection */}
            <div className="flex-1 max-w-md">
                <MultiSelect
                    options={clientOptions}
                    value={selectedValues}
                    onChange={handleClientChange}
                    placeholder="All Clients"
                />
            </div>

            {/* Right - Filter Controls */}
            <div className="flex items-center gap-2">
                {/* Achievement */}
                <div className="inline-flex rounded border border-gray-300 p-0.5 bg-gray-50">
                    <button
                        onClick={() => onAchievementFilterChange('all')}
                        className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                            achievementFilter === 'all' ? 'text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
                        }`}
                        style={achievementFilter === 'all' ? { backgroundColor: '#422e52' } : {}}
                    >
                        All
                    </button>
                    <button
                        onClick={() => onAchievementFilterChange('over')}
                        className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                            achievementFilter === 'over' ? 'text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
                        }`}
                        style={achievementFilter === 'over' ? { backgroundColor: '#422e52' } : {}}
                    >
                        Over
                    </button>
                    <button
                        onClick={() => onAchievementFilterChange('under')}
                        className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                            achievementFilter === 'under' ? 'text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
                        }`}
                        style={achievementFilter === 'under' ? { backgroundColor: '#422e52' } : {}}
                    >
                        Under
                    </button>
                </div>

                {/* Period */}
                <div className="inline-flex rounded border border-gray-300 p-0.5 bg-gray-50">
                    <button
                        onClick={() => onPeriodTypeChange('current')}
                        className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                            periodType === 'current' ? 'text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
                        }`}
                        style={periodType === 'current' ? { backgroundColor: '#422e52' } : {}}
                    >
                        Current
                    </button>
                    <button
                        onClick={() => onPeriodTypeChange('last')}
                        className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                            periodType === 'last' ? 'text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
                        }`}
                        style={periodType === 'last' ? { backgroundColor: '#422e52' } : {}}
                    >
                        Last Month
                    </button>
                </div>

                {/* Comparison */}
                <div className="inline-flex rounded border border-gray-300 p-0.5 bg-gray-50">
                    <button
                        onClick={() => onComparisonTypeChange('plan')}
                        className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                            comparisonType === 'plan' ? 'text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
                        }`}
                        style={comparisonType === 'plan' ? { backgroundColor: '#422e52' } : {}}
                    >
                        vs Plan
                    </button>
                    <button
                        onClick={() => onComparisonTypeChange('lastYear')}
                        className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                            comparisonType === 'lastYear' ? 'text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
                        }`}
                        style={comparisonType === 'lastYear' ? { backgroundColor: '#422e52' } : {}}
                    >
                        vs Last Year
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DashboardControls;

